<script setup lang="ts">
import manufacturerLogo from '@/assets/images/logo.png';
import { useAuthenticationStore } from '@/stores/authenticationStore';
import { useSystemStore } from '@/stores/systemStore';
import { EMPTY_FIELD_SIGN } from '../forms/utils';
import SimpleLayoutPrototypeWarning from '../layouts/SimpleLayout/SimpleLayoutPrototypeWarning.vue';
import ModuleInfo from './ModuleInfo.vue';
import { computed } from 'vue';
import { formatVersion } from '@/utils/stringUtils';

const systemStore = useSystemStore();
const authStore = useAuthenticationStore();

// Create non-static info about the product
const version = computed(() => formatVersion(systemStore.version));
const installationName = computed(() => systemStore.siteName ?? EMPTY_FIELD_SIGN);
const ipin = computed(() => authStore.session?.ipin ?? EMPTY_FIELD_SIGN);

const emit = defineEmits<{
  goSoupPage: [];
}>();

function onCreditsClicked() {
  emit('goSoupPage');
}
</script>

<template>
  <div class="section">
    <v-row class="mb-4">
      <v-col cols="7" class="mb-0">
        <v-row>
          <v-col>
            <p class="v-label-overwrite">
              {{ $t('InstalledVersion') }}
            </p>
            <p class="mb-4">{{ version }}</p>
          </v-col>
          <v-col>
            <p class="v-label-overwrite">
              {{ $t('InstallationName') }}
            </p>
            <p class="mb-4">{{ installationName }}</p>
          </v-col>
          <v-col>
            <p class="v-label-overwrite">
              {{ $t('InstallationPIN') }}
            </p>
            <p class="mb-4">{{ ipin }}</p>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="5" class="mb-0">
        <p class="v-label-overwrite">
          {{ $t('InstructionManualLabel') }}
        </p>

        <div class="d-flex align-center justify-start ga-1 mt-2">
          <img
            class="handbook"
            :src="`data:image/png;base64,${systemStore.instructions?.icon1}`"
            alt="ISO 7010"
          />
          <img
            class="handbook"
            :src="`data:image/png;base64,${systemStore.instructions?.icon2}`"
            alt=""
          />
          <a
            class="v-label-overwrite align-self-center"
            :href="systemStore.instructions?.link"
            target="_blank"
            >{{ systemStore.instructions?.link }}</a
          >
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col class="py-0">
        <hr class="mb-4" />
        <div class="d-flex align-center justify-start">
          <p class="text-h4">
            {{ $t('Manufacturer') }}
          </p>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="1">
        <img
          class="manufacturer"
          :src="`data:image/png;base64,${systemStore.manufacturer?.icon}`"
          alt="Manufacturer"
        />
      </v-col>

      <v-col cols="6" class="mb-0">
        <div>
          {{ systemStore.manufacturer?.name }}<br />
          {{ systemStore.manufacturer?.fullName }}<br />
          {{ systemStore.manufacturer?.contactData.street }}<br />
          {{ systemStore.manufacturer?.contactData.city }}<br />
          {{ systemStore.manufacturer?.contactData.phone }}<br />
          <a :href="`mailto:${systemStore.manufacturer?.contactData.email}`">{{
            systemStore.manufacturer?.contactData.email
          }}</a>
          <br />
          <a :href="systemStore.manufacturer?.contactData.website" target="_blank">{{
            systemStore.manufacturer?.contactData.website
          }}</a>
        </div>
      </v-col>
      <v-col cols="5" class="d-flex align-start justify-end mb-0">
        <img
          :src="manufacturerLogo"
          alt="PTW"
          style="max-width: 100%; height: auto"
          class="ptw-logo"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col class="pb-0">
        <hr class="mb-4" />
        <p class="text-h4">
          {{ $t('Included') }}
        </p>
      </v-col>
    </v-row>

    <v-row class="mb-4">
      <v-col v-for="task in systemStore.taskTypes" :key="task.nameKey ?? ''">
        <ModuleInfo :data="task" />
      </v-col>
    </v-row>

    <v-row class="mb-4">
      <v-col>
        {{ $t('SOUPLinkText') }}
        <a
          class="link"
          href="#"
          activeClass="active"
          data-nav-item
          @click.prevent="onCreditsClicked"
        >
          {{ $t('OpenSourceSoftware') }} </a
        >.
      </v-col>
    </v-row>

    <SimpleLayoutPrototypeWarning v-if="systemStore.isPrototype" class="mt-10" />
  </div>
</template>

<style lang="scss" scoped>
.manufacturer {
  margin-left: 8px;
  height: 50px;
}

.ptw-logo {
  width: 170px;
  height: auto;
}

.handbook {
  margin-right: 8px;
  width: 38px;
  height: 38px;
}
</style>
